import React, { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  ChatBubble as ChatBubbleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUser } from '../../../contexts/UserContext';
import { 
  Organization, 
  OrganizationMember, 
  OrganizationRole, 
  MemberActivityLog 
} from '../../../types/Organization';
import { formatDistanceToNow } from 'date-fns';

interface FilterOptions {
  role: string;
  status: string;
}

interface ActivityLogItemProps {
  activity: MemberActivityLog;
}

// Component to display member activity logs
const ActivityLogItem: React.FC<ActivityLogItemProps> = ({ activity }) => {
  // Helper function to format activity text
  const getActivityText = () => {
    const details = activity.details || {};
    
    switch (activity.action) {
      case 'login':
        return 'Logged in to the system';
      case 'invite_sent':
        return `Invited ${details.email || 'a new member'} as ${details.role || 'member'}`;
      case 'invitation_resent':
        return `Resent invitation to ${details.email || 'a member'}`;
      case 'invite_accepted':
        return 'Accepted invitation to join';
      case 'member_added':
        return `Added ${details.name || 'a new member'} to the organization`;
      case 'member_removed':
        return `Removed ${details.name || details.email || 'a member'} from the organization`;
      case 'member_role_changed':
        return `Changed ${details.name || details.email || 'a member'}'s role from ${
          details.oldRole || 'previous role'
        } to ${details.newRole || 'new role'}`;
      case 'member_status_changed':
        return `Changed ${details.name || details.email || 'a member'}'s status to ${
          details.newStatus || 'new status'
        }`;
      case 'event_created':
        return `Created event "${details.eventName || 'New event'}"`;
      case 'event_updated':
        return `Updated event "${details.eventName || 'an event'}"`;
      case 'event_deleted':
        return `Deleted event "${details.eventName || 'an event'}"`;
      case 'message_sent':
        return `Sent a message in ${details.channelName || 'a channel'}`;
      default:
        return `${activity.action.replace(/_/g, ' ')}`;
    }
  };
  
  return (
    <ListItem>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          {activity.action.includes('event') ? <EventIcon /> : 
           activity.action.includes('invite') || activity.action.includes('member') ? <PersonAddIcon /> :
           activity.action.includes('message') ? <ChatBubbleIcon /> :
           <HistoryIcon />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={getActivityText()}
        secondary={
          <React.Fragment>
            {activity.timestamp ? (
              formatDistanceToNow(
                new Date(activity.timestamp.seconds * 1000),
                { addSuffix: true }
              )
            ) : (
              'Unknown time'
            )}
          </React.Fragment>
        }
      />
    </ListItem>
  );
};

const OrganizationMembersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    activeOrganization, 
    members, 
    loadingMembers, 
    inviteMember, 
    removeMember, 
    updateMemberRole,
    logMemberActivity,
    getMemberActivities,
    canCurrentUserManageMembers 
  } = useOrganization();
  const { currentUser } = useUser();
  
  // State for members
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    role: 'all',
    status: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMembers, setFilteredMembers] = useState<OrganizationMember[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterApplied, setFilterApplied] = useState(false);
  
  // State for member editing
  const [memberToChangeRole, setMemberToChangeRole] = useState<OrganizationMember | null>(null);
  const [newRole, setNewRole] = useState<OrganizationRole>('member');
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  
  // State for member deletion
  const [memberToDelete, setMemberToDelete] = useState<OrganizationMember | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // State for member invitation
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationRole>('member');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [bypassVerification, setBypassVerification] = useState(false);
  
  // State for activity logs
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);
  const [activities, setActivities] = useState<MemberActivityLog[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Current user's member object 
  const currentMember = activeOrganization?.members?.find(
    member => member.userId === currentUser?.uid
  );
  
  // Get permissions flag
  const canManageMembers = canCurrentUserManageMembers();
  
  useEffect(() => {
    if (id && members) {
      setLoading(false);
      setFilteredMembers(members);
    }
  }, [id, members]);

  // Modified applyFilters function with strong debug logging
  const applyFilters = useCallback(() => {
    if (!members) {
       return;
    }
    

    let filtered = [...members];
    
    // Apply role filter
    if (filterOptions.role !== 'all') {
       filtered = filtered.filter((member) => member.role === filterOptions.role);
    }

    // Apply status filter
    if (filterOptions.status !== 'all') {
       filtered = filtered.filter((member) => member.status === filterOptions.status);
    }
    
    // Apply search filter
    if (searchQuery) {
       const query = searchQuery.toLowerCase();
       filtered = filtered.filter(
          (member) =>
             member.name?.toLowerCase().includes(query) ||
             member.displayName?.toLowerCase().includes(query) ||
             member.email.toLowerCase().includes(query)
       );
    }

    setFilterApplied(
       filterOptions.role !== 'all' || filterOptions.status !== 'all' || searchQuery !== ''
    );
    

    setFilteredMembers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [members, filterOptions, searchQuery]);

  // Make sure we apply filters whenever members or filter options change
  useEffect(() => {
     if (members) {
        applyFilters();
     }
  }, [members, filterOptions, searchQuery, applyFilters]);

  // Function to fetch members - this reloads the current members from the context
  const fetchMembers = () => {
     setLoading(true);

     // Force a re-render by setting state
     setTimeout(() => {
        if (members) {
           setFilteredMembers([...members]); // Create a new array to force re-render
        } else {
        }
        setLoading(false);
     }, 500);
  };

  const resetFilters = () => {
    setFilterOptions({
      role: 'all',
      status: 'all'
    });
    setSearchQuery('');
    setFilterApplied(false);
    
    if (members) {
      setFilteredMembers(members);
    }
  };

  // Handle page change
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    
    if (newValue === 0) {
      // All Members tab
      setSelectedMember(null);
    } else if (newValue === 1 && !selectedMember) {
      // Activity tab - load all activities if no member is selected
      fetchActivities();
    }
  };

  // Handle inviting a new member
  const handleInviteMember = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await inviteMember(id, inviteEmail, inviteRole, bypassVerification);
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setBypassVerification(false);
      setInviteSuccess(true);
      
      // Refresh members list
      fetchMembers();
      
    } catch (error: any) {
      console.error('Error inviting member:', error);
      setError(error.message || 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  // Handle removing a member
  const handleRemoveMember = async () => {
    if (!id || !memberToDelete) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await removeMember(id, memberToDelete.id);
      
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      
      // Refresh members list
      fetchMembers();
      
    } catch (error: any) {
      console.error('Error removing member:', error);
      setError(error.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  // Handle changing a member's role
  const handleChangeRole = async () => {
    if (!id || !memberToChangeRole) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await updateMemberRole(id, memberToChangeRole.id, newRole);
      
      setChangeRoleDialogOpen(false);
      setMemberToChangeRole(null);
      
      // Refresh members list
      fetchMembers();
      
    } catch (error: any) {
      console.error('Error changing role:', error);
      setError(error.message || 'Failed to change role');
    } finally {
      setLoading(false);
    }
  };

  // Function to open delete dialog
  const handleOpenDeleteDialog = (member: OrganizationMember) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  // Function to fetch member activities
  const fetchActivities = async (memberId?: string) => {
    if (!id || !getMemberActivities) return;
    
    setLoadingActivities(true);
    
    try {
      const fetchedActivities = await getMemberActivities(id, memberId);
      setActivities(fetchedActivities);
      
      if (memberId) {
        const memberToView = members?.find(m => m.id === memberId) || null;
        setSelectedMember(memberToView);
      }
      
      setSelectedTab(1); // Switch to Activity tab
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Organization Members
        </Typography>
        
        {canManageMembers && (
          <Button
            variant="contained"
            startIcon={<PersonIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>
        )}
      </Box>
      
      {/* Success message */}
      <Snackbar
        open={inviteSuccess}
        autoHideDuration={6000}
        onClose={() => setInviteSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setInviteSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Member invited successfully!
        </Alert>
      </Snackbar>

      {/* Stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" component="div">
                {members?.length || 0}
              </Typography>
              <Typography color="text.secondary">
                Total Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" component="div">
                {members?.filter(m => m.role === 'admin' || m.role === 'owner').length || 0}
              </Typography>
              <Typography color="text.secondary">
                Admins & Owners
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" component="div">
                {members?.filter(m => m.status === 'active').length || 0}
              </Typography>
              <Typography color="text.secondary">
                Active Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Organization Members section */}
      <Grid container spacing={3}>
        {/* Members list */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">All Members</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  placeholder="Search members..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mr: 2 }}
                />
                <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                  <InputLabel id="role-filter-label">Role</InputLabel>
                  <Select
                    labelId="role-filter-label"
                    name="role"
                    value={filterOptions.role}
                    label="Role"
                    onChange={(e) => setFilterOptions({ ...filterOptions, role: e.target.value })}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="member">Member</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    name="status"
                    value={filterOptions.status}
                    label="Status"
                    onChange={(e) => setFilterOptions({ ...filterOptions, status: e.target.value })}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMembers.length > 0 ? (
                      filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                src={member.avatarUrl || member.photoURL}
                                sx={{ mr: 2, bgcolor: 'primary.main' }}
                              >
                                {(member.name || member.displayName || member.email)[0].toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {member.name || member.displayName || member.email.split('@')[0]}
                                  {member.userId?.toString().startsWith('test-user') && (
                                    <Chip 
                                      size="small" 
                                      label="Test" 
                                      color="info" 
                                      sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} 
                                    />
                                  )}
                                </Typography>
                                
                                {/* Show last active time if available */}
                                {member.lastActive && (
                                  <Typography variant="caption" color="textSecondary">
                                    Last active: {formatDistanceToNow(
                                      new Date(member.lastActive.seconds * 1000),
                                      { addSuffix: true }
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={member.role}
                              size="small"
                              color={
                                member.role === 'owner'
                                  ? 'success'
                                  : member.role === 'admin'
                                  ? 'primary'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={member.status}
                              size="small"
                              color={
                                member.status === 'active'
                                  ? 'success'
                                  : member.status === 'pending'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {member.joinedAt ? (
                              new Date(member.joinedAt.seconds * 1000).toLocaleDateString()
                            ) : member.invitedAt ? (
                              <>
                                <Typography variant="caption" color="textSecondary">
                                  Invited: {new Date(member.invitedAt.seconds * 1000).toLocaleDateString()}
                                </Typography>
                                {member.status === 'pending' && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Button 
                                      size="small" 
                                      variant="outlined"
                                      onClick={() => alert(`Invitation would be resent to ${member.email}`)}
                                      sx={{ fontSize: '0.7rem', py: 0, px: 1 }}
                                    >
                                      Resend
                                    </Button>
                                  </Box>
                                )}
                              </>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex' }}>
                              {/* Only show edit button if user can manage members and target is not owner (unless current user is owner) */}
                              {canManageMembers && (
                                <Tooltip title="Edit Member">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => setMemberToChangeRole(member)}
                                    sx={{ mr: 1 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {/* View activity button */}
                              <Tooltip title="View Activity">
                                <IconButton 
                                  size="small" 
                                  onClick={() => fetchActivities(member.id)}
                                  sx={{ mr: 1 }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {/* Remove member button - don't show for owners unless current user is owner */}
                              {canManageMembers && (
                                <Tooltip title="Remove Member">
                                  <IconButton 
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenDeleteDialog(member)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                          No members found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                    Showing {filteredMembers.length} of {members?.length || 0} members
                  </Typography>
                  {filteredMembers.length > 0 && (
                    <Pagination 
                      count={Math.ceil((filteredMembers?.length || 0) / rowsPerPage)} 
                      page={page + 1} 
                      onChange={(e, newPage) => handleChangePage(e, newPage - 1)}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        
        {/* Activity feed */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {selectedMember ? 'Member Activity' : 'Recent Activity'}
            </Typography>
            
            {selectedMember && (
              <Box sx={{ mb: 2 }}>
                <Button 
                  size="small" 
                  startIcon={<ClearIcon />} 
                  onClick={() => fetchActivities()}
                >
                  Clear Selection
                </Button>
              </Box>
            )}
            
            {loadingActivities ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ width: '100%', maxHeight: 400, overflow: 'auto' }}>
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <ActivityLogItem key={activity.id} activity={activity} />
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No recent activity" />
                  </ListItem>
                )}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              margin="normal"
              required
              error={!!error}
              helperText={error}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as OrganizationRole)}
                label="Role"
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={bypassVerification}
                  onChange={(e) => setBypassVerification(e.target.checked)}
                />
              }
              label="Testing: Create member without verification"
              sx={{ mt: 1 }}
            />
            
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              {bypassVerification ? 
                "For testing only: This will create an active member without requiring email verification" : 
                "An invitation will be sent to this email address"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleInviteMember} 
            variant="contained" 
            disabled={!inviteEmail}
          >
            Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {memberToDelete?.name || memberToDelete?.email} from this organization?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRemoveMember} 
            color="error" 
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={changeRoleDialogOpen} onClose={() => setChangeRoleDialogOpen(false)}>
        <DialogTitle>Change Member Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Change the role of {memberToChangeRole?.name || memberToChangeRole?.email}:
          </DialogContentText>
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as OrganizationRole)}
              label="Role"
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeRoleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleChangeRole} 
            variant="contained" 
            disabled={memberToChangeRole?.role === newRole}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationMembersPage;